import { useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import Pagination from 'rc-pagination';
import DropDown from '@/trio-ui/Dropdown';
import * as Styles from './Pagination.styles';

export type PaginationProps = {
  count: number;
  onChangeRecordsPerPage?: (itemsPerPage: number) => void;
  onChangePageHandler: (page: number) => void;
  currentPage: number;
  itemsPerPage: number;
  lastPage: number;
  hideItemsPerPageDropdown?: boolean;
};

const recordsPerPageOptions = [
  { label: '10 Records per page', value: 10 },
  { label: '25 Records per page', value: 25 },
  { label: '50 Records per page', value: 50 },
  { label: '75 Records per page', value: 75 },
  { label: '100 Records per page', value: 100 },
];

const PaginationComponent = ({
  onChangePageHandler,
  currentPage,
  itemsPerPage,
  count,
  lastPage,
  onChangeRecordsPerPage,
  hideItemsPerPageDropdown = false,
}: PaginationProps): JSX.Element => {
  const itemRender = useCallback((current, type) => {
    if (type === 'next' || type === 'prev') {
      return (
        <Styles.PaginationButton
          data-testid={`pagination-go-${type}-button`}
          isRotated={type === 'prev'}
        >
          <Styles.SimpleArrowIcon />
        </Styles.PaginationButton>
      );
    }

    if (['jump-prev', 'jump-next'].includes(type)) {
      return <Styles.PaginationButton>...</Styles.PaginationButton>;
    }

    return (
      <Styles.PaginationButton data-testid={`go-to-page-${current}`}>
        {current}
      </Styles.PaginationButton>
    );
  }, []);

  return (
    <div data-testid="pagination">
      <Styles.Box>
        <div className="flex">
          <Styles.CountLabel>Total Count:</Styles.CountLabel>
          <Styles.CountTotal>{count}</Styles.CountTotal>
        </div>
        <Styles.PaginationContainer>
          <Box display="flex">
            <Styles.PaginationButton
              disabled={currentPage === 1}
              data-testid="go-to-first-page"
              onClick={() => onChangePageHandler(1)}
            >
              <Styles.BarArrowIcon />
            </Styles.PaginationButton>

            <Pagination
              showLessItems
              showTitle={false}
              pageSize={itemsPerPage}
              onChange={onChangePageHandler}
              current={currentPage}
              total={count}
              itemRender={itemRender}
            />

            <Styles.PaginationButton
              isRotated
              data-testid="go-to-last-page"
              disabled={currentPage === lastPage}
              onClick={() => onChangePageHandler(lastPage)}
            >
              <Styles.BarArrowIcon />
            </Styles.PaginationButton>
          </Box>

          {!hideItemsPerPageDropdown && (
            <Styles.DropdownArea data-testid="pagination-records-per-page">
              <DropDown
                optionsWithLabel={recordsPerPageOptions}
                name="records-per-page"
                defaultValue={itemsPerPage.toString()}
                isLabelAboveField
                onChangeDropDown={({ target }) => {
                  const { value } = target;
                  onChangeRecordsPerPage?.(value);
                }}
              />
            </Styles.DropdownArea>
          )}
        </Styles.PaginationContainer>
      </Styles.Box>
    </div>
  );
};

export default PaginationComponent;
